import React from "react";
import { Helmet } from "react-helmet";
import { ShareSection } from "../../components/template";

// Hashtag section
const HashtagSection = ({ tags }) => (
  <div className="mt-6 flex flex-wrap md:gap-3 gap-1 justify-center md:mb-6 mb-3">
    {tags.map((tag, index) => (
      <span
        key={index}
        className="bg-red-700 bg-opacity-10 text-red-700 px-4 py-2 rounded-full text-sm font-semibold cursor-pointer transition-all duration-300 m-1 mb-2 hover:bg-red-700 hover:text-white"
      >
        {tag}
      </span>
    ))}
  </div>
);

const Blog20 = () => {
  const hashtags = [
    "#hormonalhealth",
    "#womenswellness",
    "#selfcare",
    "#holistichealth",
    "#stressmanagement",
    "#mindbodybalance",
    "#ParamLife",
  ];

  return (
    <>
      <Helmet>
        <title>
          9 Habits Women Over 30 Should Ditch to Boost Their Hormonal Health
        </title>
        <meta
          name="description"
          content="9 everyday habits women over 30 should stop to support hormonal balance, energy, and emotional well-being — with gentle, practical shifts."
        />
      </Helmet>

      <article className="max-w-4xl mx-auto mt-10 px-6 py-4 leading-relaxed text-gray-800">
        <h1 className="text-secondary text-left text-3xl font-semibold mb-8">
           9 Habits Women Over 30 Should Ditch to Boost Their Hormonal Health
        </h1>

        <p className="text-center italic text-gray-600 mb-6">
          (Because your body isn’t rebelling — it’s just trying to tell you
          something.)
        </p>
           <img
          src="/assets/knowyourself/blogs/blog20.png"
          alt="7 Things Our Grandparents Did Right (But We Ruined)"
          className="w-[80%] max-w-xl rounded-2xl shadow-md mx-auto mb-10 ml-10"
        />


        {/* Habits */}
        <section className="space-y-6 mb-10">
          <div>
            <h3 className="text-primary font-semibold">1. Skipping meals</h3>

            <p className="text-[#578d48] font-semibold">Why it’s a problem:</p>
            <p>
              You think you’re being “disciplined.” But your body feels
              abandoned. When you skip meals, your stress hormones shoot up,
              your energy dips, and your mood follows.
            </p>

            <p className="text-[#578d48] font-semibold">What helps:</p>
            <p>
              Feed yourself — not just when it’s convenient, but when your body
              asks. Even a handful of nuts or fruit between meals keeps your
              hormones calm and your brain clear.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">2. Skipping movement</h3>

            <p className="text-[#578d48] font-semibold">Why it’s a problem:</p>
            <p>
              Your body was never built for sitting 10 hours a day. When you
              stay still too long, your blood slows down, your energy dulls, and
              your happy hormones drop.
            </p>

            <p className="text-[#578d48] font-semibold">What helps:</p>
            <p>
              You don’t need a fancy gym. A slow walk after lunch. Stretching
              before bed. A quick dance while you cook — it all counts.
              Movement is medicine.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">3. Overdoing cardio</h3>

            <p className="text-[#578d48] font-semibold">Why it’s a problem:</p>
            <p>
              If you’re punishing your body with endless runs or HIIT because
              you want to “burn fat,” your body starts burning itself out
              instead.
            </p>

            <p className="text-[#578d48] font-semibold">What helps:</p>
            <p>
              Switch to balance. Mix yoga with walks. Try strength training
              twice a week. Train your body to feel strong — not just look
              smaller.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              4. Overstimulating your brain at night
            </h3>

            <p className="text-[#578d48] font-semibold">Why it’s a problem:</p>
            <p>
              Your phone isn’t helping you “unwind.” Late-night scrolling and
              constant chatter trick your brain into staying alert — when your
              hormones need rest.
            </p>

            <p className="text-[#578d48] font-semibold">What helps:</p>
            <p>
              Create a night ritual. No screens after 9. Dim the lights. Sit in
              silence or journal. Let your body remember what calm feels like.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">5. Overdoing caffeine</h3>

            <p className="text-[#578d48] font-semibold">Why it’s a problem:</p>
            <p>
              That third coffee isn’t energy — it’s anxiety pretending to help.
              Too much caffeine spikes your cortisol, crashes your sleep, and
              stresses your adrenals.
            </p>

            <p className="text-[#578d48] font-semibold">What helps:</p>
            <p>
              One coffee is fine. But swap the rest for warm water, green tea,
              or chamomile. Energy built from calm lasts longer than energy
              built from chaos.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">6. Ignoring PMS signs</h3>

            <p className="text-[#578d48] font-semibold">Why it’s a problem:</p>
            <p>
              When you brush off cramps, mood swings, or fatigue as “normal,”
              you miss your body’s early warnings. PMS isn’t a nuisance — it’s
              communication.
            </p>

            <p className="text-[#578d48] font-semibold">What helps:</p>
            <p>
              Track your cycle. Notice patterns. During PMS, go slower,
              hydrate, and rest more. You’re not lazy — you’re listening.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              7. Sleeping less than 6 hours
            </h3>

            <p className="text-[#578d48] font-semibold">Why it’s a problem:</p>
            <p>
              You can fake productivity — but not restoration. Lack of sleep
              messes with insulin, cortisol, and estrogen — the trio that
              controls your mood and metabolism.
            </p>

            <p className="text-[#578d48] font-semibold">What helps:</p>
            <p>
              Prioritize sleep like it’s your new superpower. Make your room
              cool, dark, and screen-free. Let 7–8 hours become your healing
              ritual.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">8. Constant dieting</h3>

            <p className="text-[#578d48] font-semibold">Why it’s a problem:</p>
            <p>
              Your body doesn’t understand “cheat days” and “restriction.” It
              only knows feast or famine. Dieting too often confuses your
              metabolism and can even stop your cycle.
            </p>

            <p className="text-[#578d48] font-semibold">What helps:</p>
            <p>
              Eat for nourishment, not punishment. Think colors, not calories.
              Real food, real portions, real peace with your plate.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              9. Underestimating stress
            </h3>

            <p className="text-[#578d48] font-semibold">Why it’s a problem:</p>
            <p>
              Stress is silent chaos. It doesn’t always scream — it simmers.
              And while it simmers, it throws every hormone off balance.
            </p>

            <p className="text-[#578d48] font-semibold">What helps:</p>
            <p>
              Build small pauses into your day. Breathe deeply. Take short
              walks. Sit in stillness. You don’t have to escape your life to
              feel at peace — just reconnect with it.
            </p>
          </div>
        </section>

        {/* Final Thought */}
        <section className="space-y-4 mb-10">
          <h2 className="text-secondary text-2xl font-semibold">
            🌿 A final thought
          </h2>

          <p className="text-[#578d48]">
            Your hormones aren’t against you.
            <br />
            They’re trying to protect you — from exhaustion, from neglect, from
            the world’s endless demands.
          </p>

          <p className="text-[#578d48]">
            The question is — will you listen before they start shouting?
          </p>
        </section>

        <HashtagSection tags={hashtags} />
        <ShareSection />
      </article>
    </>
  );
};

export default Blog20;
